import { useEffect, useRef, useState } from "react";
import {
  getChapters,
  openChapter,
  removeFromBookshelf,
  saveProgress,
  type OpenChapterResult,
  type ContentKind,
  type UserSpace,
} from "../api";
import { getAdjacentChapter, pickChapterIdByIndex, type ReaderOpenRequest, type ReaderTargetRef } from "./reader-library";
import type { ReaderContentSnapshot, ReaderSessionTarget } from "./reader-session";
import { navigateTo } from "../routes";

interface ReaderChapterWorkspaceOptions {
  activeKind: ContentKind;
  setActiveKind: (kind: ContentKind) => void;
  user: UserSpace | null;
  prefetchChapterCount: number;
  setBusy: (busy: boolean) => void;
  setMessage: (message: string) => void;
  writeReaderSession: (target: ReaderSessionTarget) => void;
  writeReaderContentSnapshot: (snapshot: ReaderContentSnapshot) => void;
  setBookshelfForKind: (userId: string, kind: ContentKind) => Promise<void>;
  patchBookshelfProgress: (bookId: string, progress: {
    chapterId: string;
    chapterIndex: number;
    paragraphIndex?: number;
    charOffset?: number;
    scrollRatio?: number;
    updatedAt: string;
  }) => void;
  refreshMonitor: (userId: string) => Promise<void>;
}

export function useReaderChapterWorkspace({
  activeKind,
  setActiveKind,
  user,
  prefetchChapterCount,
  setBusy,
  setMessage,
  writeReaderSession,
  writeReaderContentSnapshot,
  setBookshelfForKind,
  patchBookshelfProgress,
  refreshMonitor,
}: ReaderChapterWorkspaceOptions) {
  const [opened, setOpened] = useState<OpenChapterResult | null>(null);
  const [openedChapters, setOpenedChapters] = useState<OpenChapterResult["chapter"][]>([]);
  const [activeChapterId, setActiveChapterId] = useState<string | null>(null);
  const [selectedBookTitle, setSelectedBookTitle] = useState("未打开章节");
  const [readerOpeningTitle, setReaderOpeningTitle] = useState<string | null>(null);
  const [readerTarget, setReaderTarget] = useState<ReaderTargetRef | null>(null);
  const [pendingShelfConfirmation, setPendingShelfConfirmation] = useState(false);
  const pendingShelfConfirmationRef = useRef(false);
  const leaveHandledRef = useRef(false);
  const [restoreParagraphIndex, setRestoreParagraphIndex] = useState<number | undefined>(undefined);
  const [restoreScrollRatio, setRestoreScrollRatio] = useState(0);
  const openedRef = useRef<OpenChapterResult | null>(null);
  const openedChaptersRef = useRef<OpenChapterResult["chapter"][]>([]);
  const readerTargetRef = useRef<ReaderTargetRef | null>(null);

  useEffect(() => {
    openedRef.current = opened;
  }, [opened]);

  useEffect(() => {
    openedChaptersRef.current = openedChapters;
  }, [openedChapters]);

  useEffect(() => {
    readerTargetRef.current = readerTarget;
  }, [readerTarget]);

  function prepareReaderOpen({ title, shouldNavigate }: { title: string; shouldNavigate: boolean }) {
    setOpened(null);
    setOpenedChapters([]);
    setActiveChapterId(null);
    setSelectedBookTitle(title);
    setReaderOpeningTitle(title);
    setMessage("正在加载正文");
    if (shouldNavigate && window.location.pathname !== "/reader") navigateTo("/reader");
  }

  function cancelReaderOpen() {
    setReaderOpeningTitle(null);
  }

  function restoreReaderSnapshot(snapshot: ReaderContentSnapshot) {
    const nextChapterId = snapshot.target.chapterId ?? snapshot.opened.chapter.chapterId;
    const nextPendingShelfConfirmation = Boolean(snapshot.pendingShelfConfirmation);
    const nextOpenedChapters = snapshot.openedChapters.length ? snapshot.openedChapters : [snapshot.opened.chapter];
    const nextReaderTarget = {
      bookId: snapshot.target.bookId,
      sourceId: snapshot.target.sourceId,
      sourceBookId: snapshot.target.sourceBookId,
    };

    if (snapshot.target.kind !== activeKind) setActiveKind(snapshot.target.kind);

    setOpened(snapshot.opened);
    setOpenedChapters(nextOpenedChapters);
    openedRef.current = snapshot.opened;
    openedChaptersRef.current = nextOpenedChapters;
    setActiveChapterId(nextChapterId);
    setSelectedBookTitle(snapshot.target.title);
    setReaderOpeningTitle(null);
    setReaderTarget(nextReaderTarget);
    readerTargetRef.current = nextReaderTarget;
    setPendingShelfConfirmation(nextPendingShelfConfirmation);
    pendingShelfConfirmationRef.current = nextPendingShelfConfirmation;
    leaveHandledRef.current = false;
    setRestoreParagraphIndex(snapshot.target.paragraphIndex);
    setRestoreScrollRatio(snapshot.target.scrollRatio ?? 0);
    writeReaderSession(snapshot.target);
  }

  async function openReaderTarget({
    userId,
    bookId,
    title,
    kind,
    source,
    chapterId,
    initialChapterIndex,
    initialParagraphIndex,
    initialScrollRatio,
    openedFromSearch,
    preserveCurrentOnError,
    shouldNavigate,
  }: ReaderOpenRequest) {
    const previousReaderState = preserveCurrentOnError && openedRef.current && readerTargetRef.current
      ? {
          opened: openedRef.current,
          openedChapters: openedChaptersRef.current,
          activeChapterId,
          selectedBookTitle,
          readerTarget: readerTargetRef.current,
          pendingShelfConfirmation: pendingShelfConfirmationRef.current,
          restoreParagraphIndex,
          restoreScrollRatio,
        }
      : null;

    prepareReaderOpen({ title, shouldNavigate });

    if (kind !== activeKind) {
      setActiveKind(kind);
    }

    setBusy(true);
    setMessage("正在打开章节");

    try {
      const chapters = chapterId === undefined ? await getChapters(userId, source.sourceId, source.sourceBookId) : undefined;
      const targetChapterId = chapterId ?? pickChapterIdByIndex(chapters ?? [], initialChapterIndex) ?? chapters?.[0]?.id;
      if (!targetChapterId) throw new Error("当前书源没有可打开的章节");

      const chapter = await openChapter(userId, source.sourceId, source.sourceBookId, targetChapterId, prefetchChapterCount);
      const currentChapter = chapter.chapters.find(item => item.id === targetChapterId);
      const nextScrollRatio = initialScrollRatio ?? 0;

      const progress = await saveProgress({
        userId,
        bookId,
        sourceId: source.sourceId,
        chapterId: targetChapterId,
        chapterIndex: currentChapter?.index ?? 0,
        paragraphIndex: initialParagraphIndex ?? 0,
        scrollRatio: nextScrollRatio,
      });
      patchBookshelfProgress(bookId, progress);
      const sessionTarget: ReaderSessionTarget = {
        bookId,
        title,
        kind,
        sourceId: source.sourceId,
        sourceBookId: source.sourceBookId,
        chapterId: targetChapterId,
        chapterIndex: currentChapter?.index ?? 0,
        paragraphIndex: initialParagraphIndex,
        scrollRatio: nextScrollRatio,
      };

      setOpened(chapter);
      setOpenedChapters([chapter.chapter]);
      setActiveChapterId(targetChapterId);
      setSelectedBookTitle(title);
      setReaderOpeningTitle(null);
      setReaderTarget({ bookId, sourceId: source.sourceId, sourceBookId: source.sourceBookId });
      const nextPendingShelfConfirmation = openedFromSearch ?? pendingShelfConfirmationRef.current;
      setPendingShelfConfirmation(nextPendingShelfConfirmation);
      pendingShelfConfirmationRef.current = nextPendingShelfConfirmation;
      leaveHandledRef.current = false;
      setRestoreParagraphIndex(initialParagraphIndex);
      setRestoreScrollRatio(nextScrollRatio);
      writeReaderSession(sessionTarget);
      writeReaderContentSnapshot({
        userId,
        user: user ?? undefined,
        target: sessionTarget,
        opened: chapter,
        openedChapters: [chapter.chapter],
        pendingShelfConfirmation: nextPendingShelfConfirmation,
      });
      await setBookshelfForKind(userId, kind);
      await refreshMonitor(userId);
      setMessage(`已预取 ${chapter.prefetchedChapterIds.length} 章`);
    } catch (error) {
      if (previousReaderState) {
        setOpened(previousReaderState.opened);
        setOpenedChapters(previousReaderState.openedChapters);
        openedRef.current = previousReaderState.opened;
        openedChaptersRef.current = previousReaderState.openedChapters;
        setActiveChapterId(previousReaderState.activeChapterId);
        setSelectedBookTitle(previousReaderState.selectedBookTitle);
        setReaderTarget(previousReaderState.readerTarget);
        readerTargetRef.current = previousReaderState.readerTarget;
        setPendingShelfConfirmation(previousReaderState.pendingShelfConfirmation);
        pendingShelfConfirmationRef.current = previousReaderState.pendingShelfConfirmation;
        setRestoreParagraphIndex(previousReaderState.restoreParagraphIndex);
        setRestoreScrollRatio(previousReaderState.restoreScrollRatio);
      }
      setReaderOpeningTitle(null);
      setMessage(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  }

  async function handleOpenChapterFromDirectory(
    chapterId: string,
    options: { forceRemote?: boolean; initialParagraphIndex?: number; initialScrollRatio?: number } = {},
  ) {
    if (!user || !readerTarget) return;

    if (!options.forceRemote && openLocalCachedChapter(chapterId)) return;

    await openReaderTarget({
      userId: user.id,
      bookId: readerTarget.bookId,
      title: selectedBookTitle,
      kind: activeKind,
      source: {
        id: `${readerTarget.bookId}:${readerTarget.sourceId}`,
        bookId: readerTarget.bookId,
        sourceId: readerTarget.sourceId,
        sourceBookId: readerTarget.sourceBookId,
      },
      chapterId,
      initialParagraphIndex: options.initialParagraphIndex ?? 0,
      initialScrollRatio: options.initialScrollRatio ?? 0,
      openedFromSearch: pendingShelfConfirmationRef.current,
      preserveCurrentOnError: true,
      shouldNavigate: false,
    });
  }

  async function handleOpenAdjacentChapter(offset: -1 | 1, fromChapterId?: string) {
    if (!opened) return;
    const nextChapter = getAdjacentChapter(opened.chapters, fromChapterId ?? activeChapterId ?? opened.chapter.chapterId, offset);

    if (!nextChapter) {
      setMessage(offset > 0 ? "已经是最后一章" : "已经是第一章");
      return;
    }

    await handleOpenChapterFromDirectory(nextChapter.id);
  }

  async function handleSaveCurrentProgress(metrics: { chapterId?: string; scrollRatio?: number; paragraphIndex?: number; charOffset?: number } = {}) {
    if (!user || !readerTarget || !opened) return;
    const fallbackChapterId = activeChapterId && opened.chapters.some(chapter => chapter.id === activeChapterId)
      ? activeChapterId
      : opened.chapter.chapterId;
    const progressChapterId = metrics.chapterId && opened.chapters.some(chapter => chapter.id === metrics.chapterId)
      ? metrics.chapterId
      : fallbackChapterId;
    const currentChapter = opened.chapters.find(chapter => chapter.id === progressChapterId);
    const paragraphIndex = metrics.paragraphIndex ?? restoreParagraphIndex ?? 0;
    const scrollRatio = metrics.scrollRatio ?? restoreScrollRatio;

    const sessionTarget: ReaderSessionTarget = {
      bookId: readerTarget.bookId,
      title: selectedBookTitle,
      kind: activeKind,
      sourceId: readerTarget.sourceId,
      sourceBookId: readerTarget.sourceBookId,
      chapterId: progressChapterId,
      chapterIndex: currentChapter?.index ?? 0,
      paragraphIndex,
      scrollRatio,
    };

    try {
      const progress = await saveProgress({
        userId: user.id,
        bookId: readerTarget.bookId,
        sourceId: readerTarget.sourceId,
        chapterId: progressChapterId,
        chapterIndex: currentChapter?.index ?? 0,
        paragraphIndex,
        charOffset: metrics.charOffset,
        scrollRatio,
      });
      patchBookshelfProgress(readerTarget.bookId, progress);
    } catch {
      // 离线时进度接口可能不可用 但本地阅读现场仍要继续更新
      setMessage("阅读进度暂未同步，已保留本地阅读现场");
    }

    setRestoreParagraphIndex(paragraphIndex);
    setRestoreScrollRatio(scrollRatio);
    setActiveChapterId(progressChapterId);
    writeReaderSession(sessionTarget);
    writeReaderContentSnapshot({
      userId: user.id,
      user,
      target: sessionTarget,
      opened,
      openedChapters,
      pendingShelfConfirmation: pendingShelfConfirmationRef.current,
    });
  }

  function openLocalCachedChapter(chapterId: string): boolean {
    if (!opened || !user || !readerTarget) return false;
    const localChapter = findLocalChapter(chapterId);
    if (!localChapter) return false;

    const currentChapter = opened.chapters.find(chapter => chapter.id === chapterId);
    const sessionTarget: ReaderSessionTarget = {
      bookId: readerTarget.bookId,
      title: selectedBookTitle,
      kind: activeKind,
      sourceId: readerTarget.sourceId,
      sourceBookId: readerTarget.sourceBookId,
      chapterId,
      chapterIndex: currentChapter?.index ?? 0,
      paragraphIndex: 0,
      scrollRatio: 0,
    };
    const nextOpenedChapters = upsertOpenedChapter(openedChapters, localChapter);
    const nextOpened: OpenChapterResult = {
      ...opened,
      chapter: localChapter,
      cachedChapterIds: uniqueValues([...(opened.cachedChapterIds ?? []), chapterId]),
      cachedChapters: upsertOpenedChapter(opened.cachedChapters ?? [], localChapter),
    };

    setOpened(nextOpened);
    setOpenedChapters(nextOpenedChapters);
    openedRef.current = nextOpened;
    openedChaptersRef.current = nextOpenedChapters;
    setActiveChapterId(chapterId);
    setReaderOpeningTitle(null);
    setRestoreParagraphIndex(0);
    setRestoreScrollRatio(0);
    writeReaderSession(sessionTarget);
    writeReaderContentSnapshot({
      userId: user.id,
      user,
      target: sessionTarget,
      opened: nextOpened,
      openedChapters: nextOpenedChapters,
      pendingShelfConfirmation: pendingShelfConfirmationRef.current,
    });
    setMessage("已从本地缓存打开");
    if (prefetchChapterCount > 0) void refreshLocalCacheWindow(chapterId);
    return true;
  }

  async function refreshLocalCacheWindow(chapterId: string) {
    if (!user || !readerTarget) return;
    const target = readerTarget;

    try {
      const refreshed = await openChapter(user.id, target.sourceId, target.sourceBookId, chapterId, prefetchChapterCount);
      const currentTarget = readerTargetRef.current;
      const currentOpened = openedRef.current;

      if (!currentOpened || !isSameReaderTarget(currentTarget, target)) return;

      const nextOpened = mergeOpenChapterCache(currentOpened, refreshed);
      const nextOpenedChapters = upsertOpenedChapters(openedChaptersRef.current, refreshed.cachedChapters?.length ? refreshed.cachedChapters : [refreshed.chapter]);

      openedRef.current = nextOpened;
      openedChaptersRef.current = nextOpenedChapters;
      setOpened(nextOpened);
      setOpenedChapters(nextOpenedChapters);
    } catch {
      // 后台补缓存失败不影响本地阅读
    }
  }

  function findLocalChapter(chapterId: string): OpenChapterResult["chapter"] | undefined {
    if (!opened) return undefined;
    if (opened.chapter.chapterId === chapterId) return opened.chapter;

    return openedChapters.find(chapter => chapter.chapterId === chapterId) ??
      opened.cachedChapters?.find(chapter => chapter.chapterId === chapterId);
  }

  function handleActiveChapterChange(chapterId: string) {
    if (!opened?.chapters.some(chapter => chapter.id === chapterId)) return;
    setActiveChapterId(chapterId);
  }

  async function handleLeaveReader() {
    if (!user || !readerTarget || !pendingShelfConfirmationRef.current || leaveHandledRef.current) return false;
    leaveHandledRef.current = true;

    try {
      const keepOnShelf = window.confirm(`是否将《${selectedBookTitle}》保留在书架中？\n取消将从书架移除`);

      if (!keepOnShelf) {
        await removeFromBookshelf(user.id, readerTarget.bookId);
        await setBookshelfForKind(user.id, activeKind);
        setMessage("已从书架移除临时阅读书籍");
      } else {
        setMessage("已保留到书架");
      }
    } finally {
      pendingShelfConfirmationRef.current = false;
      setPendingShelfConfirmation(false);
    }

    return true;
  }

  async function handleReturnToShelf() {
    const handled = await handleLeaveReader();
    if (!handled) setMessage("已返回书架");
    navigateTo("/shelf");
  }

  function handleReaderReturned() {
    setMessage("已返回书架");
  }

  return {
    opened,
    openedChapters,
    activeChapterId,
    readerOpeningTitle,
    pendingShelfConfirmation,
    selectedBookTitle,
    readerTarget,
    restoreParagraphIndex,
    restoreScrollRatio,
    prepareReaderOpen,
    cancelReaderOpen,
    openReaderTarget,
    restoreReaderSnapshot,
    handleOpenChapterFromDirectory,
    handleOpenAdjacentChapter,
    handleActiveChapterChange,
    handleLeaveReader,
    handleReaderReturned,
    handleSaveCurrentProgress,
    handleReturnToShelf,
  };
}

function upsertOpenedChapter(
  chapters: OpenChapterResult["chapter"][],
  chapter: OpenChapterResult["chapter"],
): OpenChapterResult["chapter"][] {
  return [
    ...chapters.filter(item => item.chapterId !== chapter.chapterId),
    chapter,
  ];
}

function upsertOpenedChapters(
  chapters: OpenChapterResult["chapter"][],
  nextChapters: OpenChapterResult["chapter"][],
): OpenChapterResult["chapter"][] {
  return nextChapters.reduce((current, chapter) => upsertOpenedChapter(current, chapter), chapters);
}

export function mergeOpenChapterCache(current: OpenChapterResult, refreshed: OpenChapterResult): OpenChapterResult {
  const cachedChapters = upsertOpenedChapters(
    current.cachedChapters ?? [],
    refreshed.cachedChapters?.length ? refreshed.cachedChapters : [refreshed.chapter],
  );

  return {
    ...current,
    chapters: refreshed.chapters.length ? refreshed.chapters : current.chapters,
    prefetchedChapterIds: uniqueValues([...current.prefetchedChapterIds, ...refreshed.prefetchedChapterIds]),
    cachedChapterIds: uniqueValues([...(current.cachedChapterIds ?? []), ...(refreshed.cachedChapterIds ?? []), ...cachedChapters.map(chapter => chapter.chapterId)]),
    cachedChapters,
  };
}

function isSameReaderTarget(left: ReaderTargetRef | null, right: ReaderTargetRef): boolean {
  return Boolean(left) &&
    left.bookId === right.bookId &&
    left.sourceId === right.sourceId &&
    left.sourceBookId === right.sourceBookId;
}

function uniqueValues(values: string[]): string[] {
  return [...new Set(values)];
}
